import { Activity, Eye, Pencil, ShieldPlus, Star, StarOff, UserCheck, UserX } from "lucide-react";
import type { AdminUser } from "../../lib/api";
import { Badge } from "../ui/Badge";
import { AdminDataTable, type Column, type RowAction } from "./dataTable/AdminDataTable";
import type { ListState } from "./dataTable/useListState";

export type UserAction = "suspend" | "activate" | "premium" | "free" | "delete" | "edit" | "promote" | "logs" | "preview";

type Props = {
  rows: AdminUser[];
  list: ListState;
  loading: boolean;
  selected: Set<string>;
  onSelectedChange: (next: Set<string>) => void;
  canWrite: boolean;
  canManageAdmins: boolean;
  onAction: (userId: string, action: UserAction) => void;
};

/** Students list: name and phone on the left, plan and status as badges, one primary action, the rest in the menu. */
export function UsersList(p: Props) {
  const act = (a: UserAction) => (u: AdminUser) => p.onAction(u.id, a);

  const columns: Column<AdminUser>[] = [
    { key: "plan", header: "Plan", className: "w-24 shrink-0", sortValue: (u) => u.plan, cell: (u) => <Badge variant={u.plan === "Premium" ? "warning" : "secondary"}>{u.plan}</Badge> },
    { key: "status", header: "Status", className: "w-28 shrink-0", sortValue: (u) => u.status, cell: (u) => <Badge variant={u.status === "Active" ? "success" : "destructive"}>{u.status}</Badge> },
    { key: "stream", header: "Stream", className: "w-28 shrink-0", desktopOnly: true, sortValue: (u) => u.streamName ?? "", cell: (u) => <span className="truncate text-muted-foreground">{u.streamName ?? "-"}</span> },
    {
      key: "activity", header: "Activity", className: "w-24 shrink-0", desktopOnly: true, sortValue: (u) => u.activity ?? 0,
      cell: (u) => <span className="font-semibold" title={`Quizzes ${u.quizzes ?? 0} · Past exams ${u.exams ?? 0} · COC ${u.cocs ?? 0}`}>{u.activity ?? 0}</span>,
    },
    { key: "joined", header: "Joined", className: "w-28 shrink-0", desktopOnly: true, cell: (u) => <span className="text-muted-foreground">{u.joined}</span> },
  ];

  const actions: RowAction<AdminUser>[] = [
    { key: "preview", label: "Student view", icon: <Eye size={14} />, group: 0, quick: true, onSelect: act("preview") },
    { key: "edit", label: "Edit student", icon: <Pencil size={14} />, group: 0, quick: true, onSelect: act("edit"), hidden: () => !p.canWrite },
    { key: "logs", label: "Activity and error logs", icon: <Activity size={14} />, group: 0, onSelect: act("logs"), hidden: () => !p.canWrite },
    { key: "premium", label: "Grant premium", icon: <Star size={14} />, group: 1, onSelect: act("premium"), hidden: (u) => !p.canWrite || u.plan !== "Free" },
    { key: "free", label: "Remove premium", icon: <StarOff size={14} />, group: 1, onSelect: act("free"), hidden: (u) => !p.canWrite || u.plan === "Free" },
    { key: "activate", label: "Activate", icon: <UserCheck size={14} />, group: 1, onSelect: act("activate"), hidden: (u) => !p.canWrite || u.status === "Active" },
    { key: "suspend", label: "Suspend", icon: <UserX size={14} />, group: 1, onSelect: act("suspend"), hidden: (u) => !p.canWrite || u.status !== "Active" },
    { key: "promote", label: "Promote to admin", icon: <ShieldPlus size={14} />, group: 2, onSelect: act("promote"), hidden: () => !p.canWrite || !p.canManageAdmins },
    { key: "delete", label: "Deactivate", icon: <UserX size={14} />, group: 3, danger: true, onSelect: act("delete"), hidden: () => !p.canWrite },
  ];

  return (
    <AdminDataTable<AdminUser>
      rows={p.rows}
      rowId={(u) => u.id}
      noun="students"
      loading={p.loading}
      columns={columns}
      actions={actions}
      list={p.list}
      selected={p.selected}
      onSelectedChange={p.onSelectedChange}
      onOpen={(u) => p.onAction(u.id, p.canWrite ? "edit" : "preview")}
      pageSize={30}
      primary={(u) => (p.canWrite && u.plan === "Free" && u.status === "Active" ? { label: "Grant premium", onClick: () => p.onAction(u.id, "premium") } : null)}
      titleCell={(u) => (
        <>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-primary" aria-hidden="true">
            {u.avatar}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-foreground">{u.name}</span>
            <span className="block truncate font-mono text-xs text-muted-foreground">{u.phone}{u.university ? ` · ${u.university}` : ""}</span>
          </span>
        </>
      )}
      empty={{ title: "No students yet", message: "Students appear here after they register." }}
    />
  );
}
